import { supabase } from './supabase';

/**
 * Signup funnel analytics — platform-level (pre-business) tracking.
 *
 * Every visitor to the signup experience gets a per-tab session id and a
 * persistent visitor id. Events flow through the record_signup_event
 * SECURITY DEFINER RPC (no table grants needed for anonymous visitors) into
 * signup_funnel_events, which the Super Admin "Signups" tab aggregates.
 *
 * All tracking is best-effort and never throws — analytics must never be able
 * to break signup itself.
 */

export type SignupFlow = 'standard' | 'extended' | 'nexus';
export type SignupEventType =
  | 'page_view'
  | 'step_view'
  | 'step_complete'
  | 'click'
  | 'error'
  | 'signup_completed';

/** Funnel stages for the standard flow (the extended flow adds its own). */
export type SignupStep =
  | 'landing'
  | 'account'
  | 'verify'
  | 'business'
  | 'completed'
  | (string & {});

const SESSION_KEY = 'signup_session_id';
const VISITOR_KEY = 'signup_visitor_id';
const UTM_KEY = 'signup_utm';
const ONCE_PREFIX = 'signup_evt_once:';
const FLOW_KEY = 'signup_flow';
const ANSWERS_KEY = 'extended_signup_answers';

function safeStorage(kind: 'session' | 'local'): Storage | null {
  try {
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function randomId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Per-tab funnel session (one journey through the signup). */
export function getSignupSessionId(): string {
  const store = safeStorage('session');
  if (!store) return randomId('sess');
  let id = store.getItem(SESSION_KEY);
  if (!id) {
    id = randomId('sess');
    store.setItem(SESSION_KEY, id);
  }
  return id;
}

/** Persistent visitor id (returning visitors keep it across sessions). */
export function getSignupVisitorId(): string {
  const store = safeStorage('local');
  if (!store) return randomId('vis');
  let id = store.getItem(VISITOR_KEY);
  if (!id) {
    id = randomId('vis');
    store.setItem(VISITOR_KEY, id);
  }
  return id;
}

/** UTM params captured on first touch of the session (survive step navigation). */
export function getSignupUtm(): Record<string, string> | null {
  const store = safeStorage('session');
  try {
    const cached = store?.getItem(UTM_KEY);
    if (cached) return JSON.parse(cached);
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const value = params.get(key);
      if (value) utm[key] = value.slice(0, 120);
    }
    if (Object.keys(utm).length === 0) return null;
    store?.setItem(UTM_KEY, JSON.stringify(utm));
    return utm;
  } catch {
    return null;
  }
}

/**
 * Which signup experience this tab is in. The entry page sets it (standard
 * landing vs. the extended qualifying flow) and every event tracked afterwards
 * — including the ones inside the shared useSignup hook — inherits it, so the
 * two funnels stay separable in the dashboard.
 */
export function setCurrentSignupFlow(flow: SignupFlow): void {
  try {
    safeStorage('session')?.setItem(FLOW_KEY, flow);
  } catch {
    /* non-fatal */
  }
}

export function getCurrentSignupFlow(): SignupFlow {
  try {
    const stored = safeStorage('session')?.getItem(FLOW_KEY);
    if (stored === 'extended' || stored === 'nexus') return stored;
    return 'standard';
  } catch {
    return 'standard';
  }
}

/** Persist a qualifying-question answer (extended flow) for later steps + the completion event. */
export function saveExtendedSignupAnswer(questionId: string, answer: string): void {
  try {
    const store = safeStorage('session');
    if (!store) return;
    const current = JSON.parse(store.getItem(ANSWERS_KEY) || '{}') as Record<string, string>;
    current[questionId] = answer;
    store.setItem(ANSWERS_KEY, JSON.stringify(current));
  } catch {
    /* non-fatal */
  }
}

export function getExtendedSignupAnswers(): Record<string, string> | null {
  try {
    const raw = safeStorage('session')?.getItem(ANSWERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, string>;
    return Object.keys(parsed).length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export interface TrackSignupEventArgs {
  eventType: SignupEventType;
  step?: SignupStep;
  flow?: SignupFlow;
  data?: Record<string, unknown>;
  referralCode?: string | null;
}

/** Fire-and-forget event write. Never throws, never blocks the UI. */
export function trackSignupEvent({
  eventType,
  step,
  flow = getCurrentSignupFlow(),
  data,
  referralCode,
}: TrackSignupEventArgs): void {
  try {
    if (!supabase) return;
    void supabase
      .rpc('record_signup_event', {
        p_session_id: getSignupSessionId(),
        p_visitor_id: getSignupVisitorId(),
        p_flow: flow,
        p_event_type: eventType,
        p_step: step ?? null,
        p_event_data: data ?? {},
        p_referral_code: referralCode ?? null,
        p_utm: getSignupUtm(),
        p_referrer: document.referrer ? document.referrer.slice(0, 500) : null,
        p_page_url: window.location.href.slice(0, 500),
        p_user_agent: navigator.userAgent.slice(0, 400),
      })
      .then(({ error }) => {
        if (error) console.warn('signup analytics write failed:', error.message);
      });
  } catch {
    /* analytics must never break signup */
  }
}

/**
 * Track an event at most once per session (e.g. step views shouldn't be
 * re-counted when the user navigates back and forth).
 */
export function trackSignupEventOnce(onceKey: string, args: TrackSignupEventArgs): void {
  const store = safeStorage('session');
  const key = `${ONCE_PREFIX}${onceKey}`;
  try {
    if (store?.getItem(key)) return;
    store?.setItem(key, '1');
  } catch {
    /* fall through to tracking anyway */
  }
  trackSignupEvent(args);
}
